// Supabase Realtime removed. Status polling now reads the latest row through a signed
// AWS `fetcher` (API Gateway + RDS); the legacy Supabase-stub fallback has been deleted.
import { useEffect, useRef, useCallback } from 'react';
import { useTabVisible } from './useTabVisibility';

interface UseRealtimeStatusOptions {
  // `table`/`filterColumn`/`selectColumns` are retained as accepted (ignored) hints for
  // call sites; the poll itself reads exclusively through the AWS `fetcher` below.
  table?: string;
  filterColumn?: string;
  selectColumns?: string;
  filterValue: string | undefined;
  enabled: boolean;
  statusField?: string;
  terminalStatuses?: string[];
  onUpdate: (row: Record<string, unknown>) => void;
  fallbackIntervalMs?: number;
  // Reads the latest row through a signed AWS endpoint. Returns the row to feed
  // `onUpdate`, or null when there is nothing to report.
  fetcher: () => Promise<Record<string, unknown> | null>;
}

export function useRealtimeStatus({
  filterValue,
  enabled,
  statusField = 'status',
  terminalStatuses = ['completed', 'error', 'ready', 'processed'],
  onUpdate,
  fallbackIntervalMs = 5000,
  fetcher,
}: UseRealtimeStatusOptions) {
  const tabVisible = useTabVisible();
  const fallbackRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const prevVisibleRef = useRef(tabVisible);

  const cleanup = useCallback(() => {
    if (fallbackRef.current) {
      clearInterval(fallbackRef.current);
      fallbackRef.current = null;
    }
  }, []);

  const fetchLatest = useCallback(async () => {
    if (!filterValue) return;
    // AWS-backed poll (signed API).
    const row = await fetcherRef.current();
    if (row) {
      onUpdateRef.current(row);
      const s = row[statusField] as string;
      if (terminalStatuses.includes(s)) cleanup();
    }
  }, [filterValue, statusField, terminalStatuses, cleanup]);

  useEffect(() => {
    if (!enabled || !filterValue) {
      cleanup();
      return;
    }

    fallbackRef.current = setInterval(async () => {
      if (!enabledRef.current) { cleanup(); return; }
      await fetchLatest();
    }, fallbackIntervalMs);

    return cleanup;
  }, [enabled, filterValue, fallbackIntervalMs, fetchLatest, cleanup]);

  useEffect(() => {
    if (!enabled || !filterValue) return;
    const wasHidden = !prevVisibleRef.current;
    prevVisibleRef.current = tabVisible;
    if (tabVisible && wasHidden) fetchLatest();
  }, [tabVisible, enabled, filterValue, fetchLatest]);

  return cleanup;
}
